/* oxlint-disable quantic/no-type-exports */
import { EventEmitter } from 'events';
import memoizeOne from 'memoize-one';

// deep-linking to prevent errors in specs about trying to load angular
import { reactRouterProvider } from 'FrontRoyalRouter/reactRouterProvider';
import { ReactRouterBridgeNotInitializedError } from 'FrontRoyalRouter/ReactRouterBridgeNotInitializedError';
import { type Phase } from 'RouteAnimation/RouteAnimation.types';
import { storeProvider } from 'ReduxHelpers';
import { navigationActions } from '../../navigationSlice';

/*
    The ReactRouterBridge receives state updates from react (via updateFromReact) and then exposes accessors
    into that information that can be accessed from angular things that don't have access to hooks.
*/

type SearchValue = string | number | boolean | null | undefined;

export interface ReactRouterBridgeState {
    pathname: string | null;
    searchParams: Record<string, string> | null;
    hashString: string | null;
    hostName: string | null;
    portValue: string | null;
    fullUrl: string | null;
    paramsRecord: Record<string, string> | null;
    routeName: string | null;
    routeAnimationPhase: Phase;
}

export interface ReactRouterBridgeRouteChangeStartEvent {
    nextUrl: string;
    currentUrl: string;
    defaultPrevented: boolean;
    preventDefault(): void;
}

type ReactRouterBridgeEventMap = {
    routeChangeSuccess: void;
    routeChangeStart: ReactRouterBridgeRouteChangeStartEvent;
};

export type ReactRouterBridgeEventName = keyof ReactRouterBridgeEventMap;
export type ReactRouterBridgeEventHandler<TName extends ReactRouterBridgeEventName = ReactRouterBridgeEventName> = (
    event: ReactRouterBridgeEventMap[TName],
) => void;

type SearchOptions = {
    replace?: boolean;
    merge?: boolean;
};

type UpdateSearchParamsOptions = {
    replace?: boolean;
    merge?: boolean;
};

export interface ReactRouterBridge extends ReactRouterBridgeState {
    routeAnimationPhase: Phase;
    path(newPath?: string): string;
    url(newUrl?: string): string;
    search(
        paramName?: string | Record<string, SearchValue>,
        paramValue?: SearchValue,
        options?: SearchOptions,
    ): Record<string, string> | ReactRouterBridge;
    hash(newHash?: string): string;
    host(): string | null;
    port(): string | null;
    absUrl(): string;
    params(): Record<string, string>;
    on<TName extends ReactRouterBridgeEventName>(
        eventName: TName,
        handler: ReactRouterBridgeEventHandler<TName>,
    ): () => void;
    off<TName extends ReactRouterBridgeEventName>(
        eventName: TName,
        handler: ReactRouterBridgeEventHandler<TName>,
    ): void;
    reload(): void;
    bumpRouteReloadKey(): void;
    updateFromReact(state: ReactRouterBridgeUpdate): void;
    emit<TName extends ReactRouterBridgeEventName>(eventName: TName, event: ReactRouterBridgeEventMap[TName]): boolean;
}

export type ReactRouterBridgeService = ReactRouterBridge;

interface ReactRouterBridgeLegacyState {
    hash?: string | null;
    host?: string | null;
    port?: string | null;
    params?: Record<string, string> | null;
    hashString?: string | null;
    hostName?: string | null;
    portValue?: string | null;
    paramsRecord?: Record<string, string> | null;
}

export type ReactRouterBridgeUpdate = Partial<ReactRouterBridgeState> & ReactRouterBridgeLegacyState;
const initialState: ReactRouterBridgeState = {
    pathname: null,
    searchParams: null,
    hashString: null,
    hostName: null,
    portValue: null,
    fullUrl: null,
    paramsRecord: null,
    routeName: null,
    routeAnimationPhase: 'idle',
};

function normalizeSearchValue(value: SearchValue): string | null {
    if (value === null || value === undefined) {
        return null;
    }
    return String(value);
}

function buildSearchString(params: Record<string, string> | null | undefined): string {
    if (!params || Object.keys(params).length === 0) {
        return '';
    }

    const searchParams = new URLSearchParams();
    Object.entries(params)
        .sort(([a], [b]) => a.localeCompare(b))
        .forEach(([key, value]) => {
            searchParams.set(key, value);
        });

    const serialized = searchParams.toString();
    return serialized ? `?${serialized}` : '';
}

function buildUrl(state: ReactRouterBridgeState): string {
    return `${state.pathname ?? ''}${buildSearchString(state.searchParams)}${state.hashString ?? ''}`;
}

function areSearchParamsEqual(
    next: Record<string, string>,
    current: Record<string, string> | null | undefined,
): boolean {
    const normalizedCurrent = current ?? {};
    const nextKeys = Object.keys(next);
    const currentKeys = Object.keys(normalizedCurrent);

    if (nextKeys.length !== currentKeys.length) {
        return false;
    }

    return nextKeys.every(key => normalizedCurrent[key] === next[key]);
}

const cloneParams = (params: Record<string, string> | null | undefined): Record<string, string> => ({
    ...(params ?? {}),
});

const stableParams = memoizeOne(
    cloneParams,
    (newArgs: Parameters<typeof cloneParams>, lastArgs: Parameters<typeof cloneParams>) => {
        const next = newArgs[0] ?? {};
        const prev = lastArgs[0] ?? {};
        return areSearchParamsEqual(next, prev);
    },
);

function firstDefined<T>(...values: Array<T | undefined>): T | undefined {
    for (let index = 0; index < values.length; index += 1) {
        const value = values[index];
        if (value !== undefined) {
            return value;
        }
    }
    return undefined;
}

// PendingSearchState handles the case where two different parts of the code in angular call
// search(key, value) synchronously. This causes problems by default because react-router only
// updates the url bar asynchronously, so if we do nothing special, the second update will overwrite the first.
// To fix this, we store the pending state until we detect that all pending updates have been applied to the url bar.
type PendingSearchState = {
    params: Record<string, string>;
};

class ReactRouterBridgeImpl implements ReactRouterBridge {
    private readonly emitter = new EventEmitter();

    private pendingSearchState: PendingSearchState | null = null;

    pathname: string | null = initialState.pathname;
    searchParams: Record<string, string> | null = initialState.searchParams;
    hashString: string | null = initialState.hashString;
    hostName: string | null = initialState.hostName;
    portValue: string | null = initialState.portValue;
    fullUrl: string | null = initialState.fullUrl;
    paramsRecord: Record<string, string> | null = initialState.paramsRecord;
    routeName: string | null = initialState.routeName;
    routeAnimationPhase: Phase = initialState.routeAnimationPhase;

    // See comment above PendingSearchState type
    private getCurrentSearchParams(): Record<string, string> {
        if (this.pendingSearchState) {
            return stableParams(this.pendingSearchState.params);
        }

        // Use React Router's state as the source of truth
        // This works correctly for both hash router and browser router
        return stableParams(this.searchParams ?? {});
    }

    // See comment above PendingSearchState type
    private setPendingSearchState(params: Record<string, string>): void {
        this.pendingSearchState = {
            params: { ...params },
        };
    }

    // See comment above PendingSearchState type
    private maybeFinalizePendingSearchState(): void {
        if (!this.pendingSearchState) return;
        if (areSearchParamsEqual(this.searchParams ?? {}, this.pendingSearchState?.params)) {
            this.pendingSearchState = null;
        }
    }

    private updateSearchParams(
        updates: Record<string, string | null | undefined>,
        options: UpdateSearchParamsOptions = {},
    ): void {
        const { replace = false, merge = true } = options;
        const startingParams = merge ? this.getCurrentSearchParams() : {};
        const nextParams: Record<string, string> = { ...startingParams };

        Object.entries(updates).forEach(([key, value]) => {
            if (value === null || value === undefined || value === '') {
                delete nextParams[key];
            } else {
                nextParams[key] = value;
            }
        });

        const searchParams = new URLSearchParams();
        Object.entries(nextParams)
            .sort(([a], [b]) => a.localeCompare(b))
            .forEach(([key, value]) => {
                searchParams.set(key, value);
            });

        this.setPendingSearchState(nextParams);

        reactRouterProvider.setSearchParams(searchParams, { replace });
    }

    // path() exists only to get the current pathname, but provides a sane fallback during init/unmount when reactRouterBridge.pathname is null.
    path(): string {
        return this.pathname ?? '';
    }

    // FIXME: this is almost assuredly incorrect, we cannot rely on window.location.pathname being correct in Capacitor
    url(): string {
        // Always read from window.location as the source of truth
        if (typeof window !== 'undefined' && window.location) {
            return `${window.location.pathname}${window.location.search}${window.location.hash}`;
        }

        return '';
    }

    search(
        paramName?: string | Record<string, SearchValue>,
        paramValue?: SearchValue,
        options?: SearchOptions,
    ): Record<string, string> | ReactRouterBridge {
        const { replace = false, merge = true } = options ?? {};

        if (paramName === undefined) {
            return this.getCurrentSearchParams();
        }

        if (typeof paramName === 'string') {
            const value = normalizeSearchValue(paramValue);
            const currentParams = this.getCurrentSearchParams();
            const currentValue = currentParams[paramName] ?? null;

            if (currentValue === value) {
                return this;
            }

            this.updateSearchParams({ [paramName]: value }, { merge, replace });
            return this;
        }

        const updates: Record<string, string | null> = {};
        const desiredParams: Record<string, string> = {};
        let requestedRemovalExists = false;
        const currentParams = this.getCurrentSearchParams();

        Object.keys(paramName).forEach(key => {
            const normalizedValue = normalizeSearchValue(paramName[key]);
            updates[key] = normalizedValue;

            if (normalizedValue === null) {
                if (!requestedRemovalExists && currentParams[key] !== undefined) {
                    requestedRemovalExists = true;
                }
                return;
            }

            desiredParams[key] = normalizedValue;
        });

        if (!requestedRemovalExists && areSearchParamsEqual(desiredParams, currentParams)) {
            return this;
        }

        // When setting multiple params at once, default to replacing all params (merge: false)
        this.updateSearchParams(updates, { merge: options?.merge ?? false, replace });
        return this;
    }

    hash(newHash?: string): string {
        if (newHash === undefined) {
            // Always read from window.location as the source of truth
            if (typeof window !== 'undefined' && window.location) {
                return window.location.hash.replace(/^#/, '');
            }
            return '';
        }

        const pathname = this.path();
        const search = typeof window !== 'undefined' && window.location ? window.location.search : '';
        const hash = newHash ? `#${newHash}` : '';

        reactRouterProvider.navigate(`${pathname}${search}${hash}`, { replace: true });

        return newHash ?? '';
    }

    host(): string | null {
        // Always read from window.location as the source of truth
        if (typeof window !== 'undefined' && window.location) {
            return window.location.hostname;
        }
        return null;
    }

    port(): string | null {
        // Always read from window.location as the source of truth
        if (typeof window !== 'undefined' && window.location) {
            return window.location.port;
        }
        return null;
    }

    absUrl(): string {
        // Always read from window.location as the source of truth
        if (typeof window !== 'undefined' && window.location) {
            return window.location.href;
        }
        return '';
    }

    params(): Record<string, string> {
        // Params come from React Router's route matching, so we use the cached value
        // If not initialized, return empty object
        return { ...(this.paramsRecord ?? {}) };
    }

    on<TName extends ReactRouterBridgeEventName>(
        eventName: TName,
        handler: ReactRouterBridgeEventHandler<TName>,
    ): () => void {
        this.emitter.on(eventName, handler);
        return () => {
            this.off(eventName, handler);
        };
    }

    off<TName extends ReactRouterBridgeEventName>(
        eventName: TName,
        handler: ReactRouterBridgeEventHandler<TName>,
    ): void {
        this.emitter.off(eventName, handler);
    }

    // Forces angular2react route components to remount without navigating (see useAngularToReactRoute).
    // Safe to call while a navigation is in flight, unlike reload().
    bumpRouteReloadKey(): void {
        storeProvider.store?.dispatch(navigationActions.setRouteReloadKey(Date.now()));
    }

    reload(): void {
        try {
            this.bumpRouteReloadKey();
            // Derive the URL from committed router state rather than window.location, which
            // cannot be trusted in Capacitor's HashRouter (see the FIXME on url()). Note that
            // committed router state is stale while a navigation is in flight, so reload() is
            // only safe for standalone callers; code that just navigated should call
            // bumpRouteReloadKey() instead.
            const currentUrl = this.fullUrl ?? this.url();
            reactRouterProvider.navigate(currentUrl, { replace: true, state: { refreshTimestamp: Date.now() } });
        } catch (error) {
            // If someone calls `reload()` before we've set up the router, then it doesn't matter. There is no path
            // to reload yet anyway. We hit this from the initialization in locale.js on each load.
            if (error instanceof ReactRouterBridgeNotInitializedError) return;
            throw error;
        }
    }

    updateFromReact(state: ReactRouterBridgeUpdate): void {
        const previousPathname = this.pathname;

        const pathnameUpdate = firstDefined(state.pathname);
        if (pathnameUpdate !== undefined) {
            this.pathname = pathnameUpdate;
        }

        const searchParamsUpdate = firstDefined(state.searchParams);
        if (searchParamsUpdate !== undefined) {
            this.searchParams = searchParamsUpdate;
        }

        this.maybeFinalizePendingSearchState();

        const hashStringUpdate = firstDefined(state.hashString, state.hash);
        if (hashStringUpdate !== undefined) {
            this.hashString = hashStringUpdate;
        }

        const hostNameUpdate = firstDefined(state.hostName, state.host);
        if (hostNameUpdate !== undefined) {
            this.hostName = hostNameUpdate;
        }

        const portValueUpdate = firstDefined(state.portValue, state.port);
        if (portValueUpdate !== undefined) {
            this.portValue = portValueUpdate;
        }

        const fullUrlUpdate = firstDefined(state.fullUrl);
        if (fullUrlUpdate !== undefined) {
            this.fullUrl = fullUrlUpdate;
        } else if (pathnameUpdate !== undefined || searchParamsUpdate !== undefined || hashStringUpdate !== undefined) {
            this.fullUrl = buildUrl(this);
        }

        const paramsRecordUpdate = firstDefined(state.paramsRecord, state.params);
        if (paramsRecordUpdate !== undefined) {
            this.paramsRecord = paramsRecordUpdate;
        }
        if (state.routeName !== undefined) {
            this.routeName = state.routeName;
        }
        if (state.routeAnimationPhase !== undefined) {
            this.routeAnimationPhase = state.routeAnimationPhase;
        }

        if (previousPathname !== this.pathname) {
            this.emit('routeChangeSuccess', undefined);
        }
    }

    emit<TName extends ReactRouterBridgeEventName>(eventName: TName, event: ReactRouterBridgeEventMap[TName]): boolean {
        return this.emitter.emit(eventName, event);
    }
}

export function reactRouterBridgeFactory(): ReactRouterBridge {
    return new ReactRouterBridgeImpl();
}

export function registerReactRouterBridge(module: angular.IModule): void {
    module.factory('reactRouterBridge', reactRouterBridgeFactory);
}
